import { useMemo, useState } from 'react'
import { Vega } from 'react-vega'

import type { MatrixResult, Occupancy, Quadrant } from '../lib/api'
import {
  OCCUPANCY_COLORS,
  OCCUPANCY_LABELS,
  POINT_SELECTION,
  buildMatrixSpec,
  topPoints as rankTop,
} from '../lib/matrixSpec'
import { Tip } from './Tip'

interface Props {
  result: MatrixResult
  selectedId: string | null
  onSelect: (territoryId: string | null) => void
}

const QUADRANT_LABELS: Record<string, string> = {
  territorio_branco: 'Território branco',
  campo_de_batalha: 'Campo de batalha',
  fortaleza: 'Fortaleza',
  descarte: 'Descarte',
}

export function MatrixChart({ result, selectedId, onSelect }: Props) {
  const [query, setQuery] = useState('')
  const [occupancy, setOccupancy] = useState<Occupancy | 'all'>('all')
  const [quadrant, setQuadrant] = useState<Quadrant | 'all'>('all')

  const filteredResult = useMemo<MatrixResult>(() => {
    const normalized = query.trim().toLocaleLowerCase('pt-BR')
    const points = result.points.filter((point) => {
      const matchesQuery = !normalized || `${point.label} ${point.pillar}`.toLocaleLowerCase('pt-BR').includes(normalized)
      return matchesQuery && (occupancy === 'all' || point.brand_occupancy === occupancy)
        && (quadrant === 'all' || point.quadrant === quadrant)
    })
    return { ...result, points }
  }, [occupancy, quadrant, query, result])

  const spec = useMemo(() => buildMatrixSpec(filteredResult, 610), [filteredResult])
  const topPoints = useMemo(
    () => rankTop(filteredResult.points, 6),
    [filteredResult],
  )

  const signalListeners = useMemo(
    () => ({
      [POINT_SELECTION]: (_name: string, value: unknown) => {
        const selection = value as { _vgsid_?: number[] } | null
        const vgsid = selection?._vgsid_?.[0]
        if (vgsid === undefined) {
          onSelect(null)
          return
        }
        const point = filteredResult.points[vgsid - 1]
        onSelect(point ? point.territory_id : null)
      },
    }),
    [filteredResult, onSelect],
  )

  return (
    <section className="matrix-section" aria-labelledby="matrix-title">
      <header className="section-heading">
        <div className="section-label">
          <span>02</span>
          <p>Mapa de territórios</p>
        </div>
        <div>
          <p className="eyebrow">A audiência encontra a competição</p>
          <h2 id="matrix-title">Onde existe afinidade — e quem já ocupa esse espaço?</h2>
        </div>
        <p className="section-deck">
          Cada bolha é um território. Posição, tamanho e cor respondem perguntas diferentes;
          selecione um ponto para abrir sua evidência completa.
        </p>
      </header>

      <div className="matrix-toolbar">
        <label className="search-control">
          <span>Buscar território</span>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Ex.: música, bem-estar…"
          />
        </label>
        <label>
          <span>Quadrante</span>
          <select value={quadrant} onChange={(event) => setQuadrant(event.target.value as Quadrant | 'all')}>
            <option value="all">Todos os quadrantes</option>
            {Object.entries(QUADRANT_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label>
          <span>Ocupação</span>
          <select value={occupancy} onChange={(event) => setOccupancy(event.target.value as Occupancy | 'all')}>
            <option value="all">Todas as ocupações</option>
            {Object.entries(OCCUPANCY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <div className="result-count" aria-live="polite">
          <strong>{filteredResult.points.length}</strong>
          <span>de {result.points.length}</span>
        </div>
      </div>

      <div className="matrix-frame">
        <div className="chart-wrap" aria-label="Matriz de Ativação">
          {/* Os eixos moram dentro do SVG do Vega; a explicação fica nesta legenda em HTML. */}
          <dl className="chart-key">
            <div><dt>X</dt><dd>Afinidade <Tip term="afinidade" /></dd></div>
            <div><dt>Y</dt><dd>Afinidade competitiva <Tip term="saturacao" /></dd></div>
            <div><dt>Tamanho</dt><dd>Alcance <Tip term="alcance" /></dd></div>
            <div><dt>Cor</dt><dd>Ocupação <Tip term="ocupacao" /></dd></div>
          </dl>
          {filteredResult.points.length > 0 ? (
            <Vega spec={spec} signalListeners={signalListeners} actions={false} renderer="svg" />
          ) : (
            <div className="chart-empty">
              <span>0</span>
              <p>Nenhum território corresponde a estes filtros.</p>
              <button type="button" onClick={() => { setQuery(''); setOccupancy('all'); setQuadrant('all') }}>
                Limpar filtros
              </button>
            </div>
          )}
        </div>

        <aside className="priority-rail" aria-label="Prioridades no recorte atual">
          <div className="rail-head">
            <span>Ranking</span>
            <span>Score <Tip term="prioridade" align="end" /></span>
          </div>
          <ol>
            {topPoints.map((point, index) => (
              <li key={point.territory_id}>
                <button
                  type="button"
                  className={selectedId === point.territory_id ? 'active' : undefined}
                  onClick={() => onSelect(point.territory_id)}
                >
                  <span className="rank-number">{String(index + 1).padStart(2, '0')}</span>
                  <span className="rank-name">
                    <strong>{point.label}</strong>
                    <small>{point.pillar}</small>
                  </span>
                  <span className="rank-score">
                    {(point.adjusted_priority_score ?? point.priority_score).toFixed(2)}
                  </span>
                </button>
              </li>
            ))}
          </ol>
          <p>Ordenado pela função do KPI, não pela afinidade isolada. Descartes ficam fora do ranking.</p>
        </aside>
      </div>

      <footer className="matrix-foot">
        <div className="occupancy-legend">
          {Object.entries(OCCUPANCY_LABELS).map(([value, label]) => (
            <span key={value}><i style={{ background: OCCUPANCY_COLORS[value] }} />{label}</span>
          ))}
        </div>
        <p>
          <i className="dash-sample" /> contorno tracejado = afinidade competitiva com base fina
          <span>·</span> preenchimento esmaecido = ocupação incerta. O dado continua no mapa,
          acompanhado da limitação.
        </p>
      </footer>
    </section>
  )
}
